import { Box, Menu, Stack } from '@mui/material';
import { IconButton, Typography } from '@sweep-io/sweep-design';
import { ReactNode, useEffect, useState } from 'react';
import { MoreActionsHorizontal } from '@sweep-io/sweep-design/dist/icons';
import usePopover from '../../hooks/usePopover';
import ChatMenuActions from './ChatMenuActions';
import RenameChatDialog from './RenameChatDialog';
import { CenteredCircularProgress } from '../common/CenteredCircularProgress';
import Typewriter from '../common/Typewriter';
import { ChatShort } from '../common/ai-chat/aiChatTypes';
import useAgentsChats from './useAgentsChats';
import { AgentMenuItem } from './AgentMenuItem';
import { TruncatedTextTooltip } from '../common/TruncatedTextTooltip';
import { useChatPdfExport } from './useChatPdfExport';
import { ShareChatDialog } from './ShareChatDialog';
import { useSelector } from 'react-redux';
import { selectDefaultCreationCrmOrgId } from '../../reducers/userInfoReducer';
import { selectActiveAgentId } from './agentsChatsReducer';
import { useFeatureToggle } from '../../feature-flags/useFeatureToggle';

const AgentChatListItem = ({
  isActive,
  title,
  subtitle,
  onClick,
  disableActions,
  chat,
}: {
  chat: ChatShort;
  isActive: boolean;
  title?: ReactNode;
  subtitle?: ReactNode;
  onClick: () => void;
  disableActions?: boolean;
}) => {
  const { renameChat, deleteChat, togglePin } = useAgentsChats();
  const { exportChatToPdf } = useChatPdfExport();
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const { closePopover, openPopover, isPopoverOpen, anchorRef, anchorElement } = usePopover();
  const activeAgentId = useSelector(selectActiveAgentId);
  const crmOrgId = useSelector(selectDefaultCreationCrmOrgId);
  const { shareChat } = useFeatureToggle();

  const { id, name, isLoading, isFavorite, isAnimateName } = chat;

  const menuIcon = (
    <IconButton ref={anchorRef} onClick={openPopover} variant="flat" size="tiny">
      <MoreActionsHorizontal />
    </IconButton>
  );

  const [isShowTypewriter, setIsShowTypewriter] = useState(false);
  const textToRender = title ?? name;

  useEffect(() => {
    setIsShowTypewriter(!!isAnimateName);
  }, [name, isAnimateName]);

  const showMenuIcon = !disableActions && (isHovered || isPopoverOpen);

  const onDelete = () => deleteChat(id);
  const onToggle = () => togglePin(id);
  const onRename = (name: string) => renameChat({ chatId: id, name });
  const onExportToPdf = async () => await exportChatToPdf(id, name);
  const onShare = () => setIsShareDialogOpen(true);

  return (
    <>
      <AgentMenuItem
        onClick={onClick}
        onMouseEnter={() => setIsHovered?.(true)}
        onMouseLeave={() => setIsHovered?.(false)}
        isSelected={isActive}
        isDisabled={false}
      >
        {isLoading ? (
          <CenteredCircularProgress size={20} />
        ) : (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              minWidth: 0,
            }}
          >
            <Stack gap={0.5} overflow="hidden">
              <TruncatedTextTooltip variant="body">
                {isShowTypewriter ? <Typewriter>{textToRender}</Typewriter> : textToRender}
              </TruncatedTextTooltip>
              {subtitle && (
                <Box
                  sx={{
                    overflow: 'hidden',
                    '& .MuiTypography-root': {
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      whiteSpace: 'break-spaces',
                      wordBreak: 'break-word',
                    },
                  }}
                >
                  <Typography variant="caption">{subtitle}</Typography>
                </Box>
              )}
            </Stack>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                minHeight: '24px',
                flex: '0 0 24px',
              }}
            >
              {showMenuIcon && menuIcon}
            </Box>
          </Box>
        )}
      </AgentMenuItem>
      <Menu
        anchorEl={anchorElement}
        open={isPopoverOpen}
        onClose={closePopover}
        slotProps={{
          paper: {
            sx: {
              width: '157px',
            },
          },
        }}
      >
        <ChatMenuActions
          closeMenu={closePopover}
          onRename={() => setIsRenameDialogOpen(true)}
          onDelete={onDelete}
          togglePin={onToggle}
          isStarred={isFavorite}
          onExportToPdf={onExportToPdf}
          onShare={shareChat ? onShare : undefined}
        />
      </Menu>

      <RenameChatDialog
        isOpen={isRenameDialogOpen}
        initialName={name}
        onChange={onRename}
        closeDialog={() => setIsRenameDialogOpen(false)}
      />
      
      {shareChat && activeAgentId && (
        <ShareChatDialog
          isOpen={isShareDialogOpen}
          onClose={() => setIsShareDialogOpen(false)}
          agentId={activeAgentId}
          chatId={id}
          crmOrgId={crmOrgId ?? undefined}
        />
      )}
    </>
  );
};

export default AgentChatListItem;
